import * as React from "react";
import { Tooltip, OverlayTrigger } from "react-bootstrap";
import { Scope, Option, useFormState, useFormApi } from "informed";
import { Required } from "../../forms/validation/Required";
import { Dropdown } from "../../forms/informedv2/Dropdown";
import { TextArea } from "../../forms/informedv2/TextArea";
import Loading, { FontSize } from "../../misc/components/loading";
import { DcfRecordState, Correction, MiscCorrection, DcfRequestState } from "./DcfRequestReducer";
import FileAttachmentsUploader from "../../file-attachments/components/uploader/FileAttachmentsUploader";
import { AttachmentRelatesTo } from "../../file-attachments/models";
import { CorrectionTypeSelection } from '../Execution/CorrectionTypeSelection';
import { CorrectionPreviewPanel } from '../Execution/CorrectionsPreviewPanel';
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { QueryOfVisit, Subject, WebPortalClient, QueryOfSession, Session, Visit } from "webportalclient";
import { DataCorrectionRequestFormFields } from "./DataCorrectionRequestForm";
import { DcfRequestAction, LoadingSessionOptionsAction, LoadingVisitOptionsAction, LoadSessionOptionsAction, LoadVisitOptionsAction } from "./DcfRequestActions";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { GetSessionLabel } from "./Selectors/SiteOptions";
import { ConfigurableDataItemIds } from "../../misc/api/ApiConfigurableDataItems";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";

export interface DataCorrectionRequestFormRecordProps {
    recordId: string;
    setRecordFileAttachmentUploader: (
        recordIndex: number,
        upload: any
    ) => void;
    onFileAttachmentChanged: (fa: any[]) => void;
    recordIndex: number;
    subjectOptions: Subject[];
    visits: DcfRecordState<QueryOfVisit>;
    sessions: DcfRecordState<QueryOfSession>;
    isLoadingSubjectOptions: boolean;
    isRemoveRecordButtonDisabled: boolean;
    onRemoveClick: (id: string) => void;
    onSubjectChange: (subjectId: string) => void;
    onVisitChange: (visitId: string) => void;
    onSessionChange: (sessionId: string) => void;
    selectedSubject: Subject;
    selectedVisit: Visit;
    selectedSession: Session;
    corrections: Correction[];
    miscCorrection: MiscCorrection;
    dispatch: React.Dispatch<DcfRequestAction>;
    dcfRequestState: DcfRequestState;
}

/**
 * Removes the selected properties for all or a selected correction record optionally specified by an index.
 * @param formApi is the Informed library formApi object.
 * @param propNames is a string array of CorrectionRecord properties to be removed.
 * @param recordIndex is an optional argument which specifes only a specific correction record for property removal.
 */
export const removeCorrectionRecordProperties = (
    formApi: any,
    propNames: string[],
    recordIndex?: number
) => {
    const CorrectionRecords = formApi.getValue("CorrectionRecords") as any[];

    if (CorrectionRecords && CorrectionRecords.length && CorrectionRecords.length > 0) {
        const removeProps = record =>
            record && propNames.forEach(propName => delete record[propName]);

        recordIndex
            ? removeProps(CorrectionRecords[recordIndex])
            : CorrectionRecords.forEach(removeProps);

        formApi.setState({ values: { ...formApi.getState().values, CorrectionRecords } });
    }
};

export const DataCorrectionRequestFormRecord = (props: DataCorrectionRequestFormRecordProps) => {
    const { dataItemConfigurationCollection } = useCurrentStudyConfigurationContext();
    const formState = useFormState<DataCorrectionRequestFormFields>();
    const formApi = useFormApi<DataCorrectionRequestFormFields>();
    const client = useWebPortalApiClient();

    React.useEffect(() => {
        const getVisits = async () => {
            if (formState.values.CorrectionRecords && props.selectedSubject) {
                props.dispatch(LoadingVisitOptionsAction.create(props.recordId));
                const visitResponse = await client.client.getVisits(`created,modified,visitScheduleItemId,id,subjectId,name,occurred`, `subjectId eq ${props.selectedSubject.id}`, "name");
                props.dispatch(LoadVisitOptionsAction.create(props.recordId, visitResponse));
            }
        }

        getVisits();
    }, [formState.values.CorrectionRecords, props.selectedSubject]);

    React.useEffect(() => {
        const getSessions = async () => {
            if (formState.values.CorrectionRecords && props.selectedVisit && props.selectedSubject) {
                props.dispatch(LoadingSessionOptionsAction.create(props.recordId));
                const sessionResponse = await client.client.getSessions(undefined, `visitId eq ${props.selectedVisit?.id}`, "name");
                props.dispatch(LoadSessionOptionsAction.create(props.recordId, sessionResponse));
            }
        }

        getSessions();
    }, [formState.values.CorrectionRecords, props.selectedVisit, props.selectedSubject]);

    React.useEffect(() => {
        if (!props.miscCorrection)
            return;
        var CorrectionRecords = formApi.getState().values.CorrectionRecords;
        if (!CorrectionRecords || !CorrectionRecords[props.recordIndex]) {
            CorrectionRecords = [];
            CorrectionRecords[props.recordIndex] = {
                ChangeBackground: null,
                SessionId: null,
                SessionTypeId: null,
                SubjectId: null,
                VisitId: null
            };
            formApi.setValues({...formApi.getState().values, CorrectionRecords});
        }
    }, [props.miscCorrection])


    const queryDataSelector = (recordState: DcfRecordState<any>): any[] => {
        if (!recordState || !recordState.query || !recordState.query.value) {
            return [];
        }

        return recordState.query.value;
    };

    const changeBackgroundTooltip = (
        <Tooltip id="changeBackgroundTooltip" style={{ zIndex: 2500 }}>
            Enter additional information that may help the change be processed promptly
        </Tooltip>
    );

    const allOptionValue = "All";

    const [allVisitsSelected, setAllVisitsSelected] = React.useState(false);
    const [allSessionsSelected, setAllSessionsSelected] = React.useState(false);

    const hasCorrections = () => {
        return props.corrections && props.corrections.length > 0 || !!props.miscCorrection;
    };

    const subjectConfig = () => dataItemConfigurationCollection.find(dic => dic.configurableDataItemId == ConfigurableDataItemIds.SubjectId);

    return (
        <div data-recordid={props.recordId}>
            <div className="panel panel-default">
                <div className="panel-body">
                    <Scope scope={`CorrectionRecords[${props.recordIndex}]`}>
                        <div className="row">
                            <div className="col-md-12">
                                <div className="form-group col-md-3">
                                    <label
                                        htmlFor={`select-subjectNumber-${props.recordId}`}
                                    >
                                        {subjectConfig().displayText}
                                    </label>
                                    &nbsp;
                                    {props.isLoadingSubjectOptions ? (
                                        <Loading
                                            className="ml-1"
                                            style={{ display: "inline-block" }}
                                            size={FontSize.OneX}
                                        />
                                    ) : null}
                                    <Dropdown
                                        disabled={props.isLoadingSubjectOptions || hasCorrections()}
                                        id={`select-subjectNumber-${props.recordId}`}
                                        field="SubjectId"
                                        className="form-control"
                                        onChange={e => {
                                            //Changing a subject resets the dropdowns
                                            setAllVisitsSelected(false);
                                            setAllSessionsSelected(false);
                                            props.onSubjectChange(e.target.value);
                                            formApi.setValue(`CorrectionRecords[${props.recordIndex}].SessionId`, "");
                                        }}
                                    >
                                        <Option key={"OptionSelect"} value="" />
                                        {props.subjectOptions.map(s => (
                                            <Option key={`Subject${s.id}`} value={s.id}>
                                                {s.number}
                                            </Option>
                                        ))}
                                    </Dropdown>
                                </div>

                                <div className="form-group col-md-3">
                                    <label htmlFor="select-visit">Visit</label>&nbsp;
                                    {props.visits != null ? (
                                        props.visits.isLoading &&
                                            props.dcfRequestState.isLoadingVisits ? (
                                            <Loading
                                                className="ml-1"
                                                style={{ display: "inline-block" }}
                                                size={FontSize.OneX}
                                            />
                                        ) : null
                                    ) : null}
                                    <Dropdown
                                        disabled={
                                            (props.dcfRequestState.isLoadingVisits &&
                                                props.visits &&
                                                props.visits.isLoading) || hasCorrections()
                                        }
                                        id={`VisitId${props.recordId}`}
                                        field="VisitId"
                                        className="form-control"
                                        onChange={e => {
                                            if (e.target.value === allOptionValue) {
                                                setAllVisitsSelected(true);
                                            } else {
                                                //De-selecting visits de-selects the session also.
                                                setAllVisitsSelected(false);
                                                setAllSessionsSelected(false);
                                            }
                                            props.onVisitChange(e.target.value);
                                        }}
                                    >
                                        <Option key={"OptionSelect"} value="" />
                                        {queryDataSelector(props.visits).map(v => (
                                            <Option key={`Visit${v.id}`} value={v.id}>
                                                {v.name}
                                            </Option>
                                        ))}
                                    </Dropdown>
                                </div>

                                <div className="form-group col-md-3">
                                    <label htmlFor="select-session">Session</label>&nbsp;
                                    {props.sessions != null ? (
                                        props.dcfRequestState.isLoadingSessions ? (
                                            <Loading
                                                className="ml-1"
                                                style={{ display: "inline-block" }}
                                                size={FontSize.OneX}
                                            />
                                        ) : null
                                    ) : null}
                                    <Dropdown
                                        disabled={
                                            props.dcfRequestState.isLoadingSessions || hasCorrections()
                                        }
                                        id={`SessionId${props.recordId}`}
                                        field="SessionId"
                                        className="form-control"
                                        onChange={e => {
                                            setAllSessionsSelected(e.target.value === allOptionValue);
                                            props.onSessionChange(e.target.value);
                                        }}
                                    >
                                        <Option key={"OptionSelect"} value="" />
                                        {queryDataSelector(props.sessions).map((s: Session) => (
                                            <Option key={`Session${s.id}`} value={s.id}>
                                                {GetSessionLabel(s)}
                                            </Option>
                                        ))}
                                    </Dropdown>
                                </div>
                                <div className="form-group col-md-3 text-right">
                                    <button
                                        className="btn btn-danger btn-outline"
                                        type="button"
                                        disabled={props.isRemoveRecordButtonDisabled}
                                        onClick={() =>
                                            props.onRemoveClick(props.recordId)
                                        }
                                    >
                                        <i className="fa fa-times" /> Remove Record
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="form-group col-md-12">
                                <label htmlFor="changeBackground">Additional Comments</label>
                                &nbsp;
                                <OverlayTrigger
                                    placement="top"
                                    overlay={changeBackgroundTooltip}
                                >
                                    <i
                                        aria-hidden="true"
                                        className="fa fa-info-circle text-muted"
                                    />
                                </OverlayTrigger>
                                <TextArea
                                    id={`changeBackground${props.recordId}`}
                                    field="ChangeBackground"
                                    className="form-control"
                                />
                            </div>
                        </div>
                        <CorrectionTypeSelection
                            allSessionsSelected={allSessionsSelected}
                            allVisitsSelected={allVisitsSelected}
                            recordId={props.recordId}
                            corrections={props.dcfRequestState.corrections.get(props.recordId)}
                            miscCorrection={props.dcfRequestState.miscCorrection.get(props.recordId)}
                            subject={props.dcfRequestState.selectedSubjects.get(props.recordId)}
                            session={props.dcfRequestState.selectedSessions.get(props.recordId)}
                            visit={props.dcfRequestState.selectedVisits.get(props.recordId)}
                            dispatch={props.dispatch}
                        />
                        <CorrectionPreviewPanel corrections={props.corrections} miscCorrection={props.miscCorrection} />
                        <div className="row">
                            <FileAttachmentsUploader
                                onFileAttachmentsUpdated={(fa) => props.onFileAttachmentChanged(fa)}
                                attachmentRelatesTo={AttachmentRelatesTo.DcfRequest}
                                requiresPermissions={[PermissionEnum.UploadDcfAttachments]}
                            />
                        </div>
                    </Scope>
                </div>
            </div>
        </div>
    );
}
